import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE, CHART_TYPE } from '../renderTypes'
import { FilterOperator, OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import { DashboardModuleType, type DashboardConfig } from '~/types/dashboards'

export const createSpeedDashboardConfig = (): DashboardConfig => {
  return [
    {
      titleKey: 'dashboard.speed.speedMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToFirstFill.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_FIRST_FILL,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_FIRST_FILL
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_FIRST_FILL]
            }
          }
        },
        {
          moduleType: DashboardModuleType.LARGE_CHART,
          options: {
            config: {
              titleKey: 'dashboard.speed.stagesDurationFunnel.title',
              infoContent: {
                definition:
                  'dashboard.speed.stagesDurationFunnel.info.definition',
                source: SourceType.ATS,
                insight: 'dashboard.speed.stagesDurationFunnel.info.insight'
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_APPLICATION_START_TO_SCREENING,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_SCREENING_TO_STEP_1,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_STEP_1_TO_STEP_2,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_STEP_2_TO_STEP_3,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_STEP_3_TO_STEP_4,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_INTERVIEW_END_TO_OFFER,
                MEASURE_TITLES.RECRUITMENT_AVERAGE_STEP_TIME_FROM_OFFER_TO_HIRE
              ]
            },
            chartType: CHART_TYPE.PIPE
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToOffer.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_OFFER,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_OFFER
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_OFFER]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToHire.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.speed.additionalMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgHiresByRecruiter.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_HIRES_BY_RECRUITER,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_HIRES_BY_RECRUITER
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_HIRES_BY_RECRUITER]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToBeginInterview.title',
              exploreLink: {
                measure:
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_BEGIN_INTERVIEW,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_BEGIN_INTERVIEW
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_BEGIN_INTERVIEW
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToInterview.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_INTERVIEW,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_INTERVIEW
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_INTERVIEW]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DISTRIBUTION,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgHiresByRecruiterDistribution.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_HIRE,
                type: EXPLORER_TYPE.DOUGHNUT,
                dimension:
                  DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME
              },
              infoContent: {
                definition:
                  'dashboard.speed.avgHiresByRecruiterDistribution.info.definition',
                source: SourceType.ATS,
                insight:
                  'dashboard.speed.avgHiresByRecruiterDistribution.info.insight'
              }
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_COUNT_HIRE],
              dimensions: [
                DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME
              ],
              filters: [
                {
                  member:
                    DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME,
                  operator: FilterOperator.SET,
                  values: []
                }
              ],
              order: [[MEASURE_TITLES.RECRUITMENT_COUNT_HIRE, OrderValue.DESC]],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.DISTRIBUTION,
          options: {
            config: {
              titleKey: 'dashboard.speed.avgTimeToHireByRecruiter.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE,
                dimension:
                  DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME,
                type: EXPLORER_TYPE.KPI
              },
              infoContent: {
                definition:
                  'dashboard.speed.avgTimeToHireByRecruiter.info.definition',
                source: SourceType.ATS,
                insight: 'dashboard.speed.avgTimeToHireByRecruiter.info.insight'
              }
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE],
              dimensions: [
                DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME
              ],
              filters: [
                {
                  member:
                    DIMENSION_TITLES.JOB_APPLICATION_RECRUITER_DISPLAY_NAME,
                  operator: FilterOperator.SET,
                  values: []
                },
                {
                  member: MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE,
                  operator: FilterOperator.SET,
                  values: []
                }
              ],
              order: [
                [
                  MEASURE_TITLES.RECRUITMENT_AVERAGE_TIME_TO_HIRE,
                  OrderValue.ASC
                ]
              ],
              limit: 5
            }
          }
        }
      ]
    }
  ]
}
